import { useCallback, useEffect, useRef, useState } from 'react';
import { clearInterval, setInterval } from 'worker-timers';

import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import { getIsMobile, isSafari } from 'utils/browsers';
import constants from 'utils/Constants';
import logger from 'utils/logging/faro';

import useTabBroadcastChannel from './useTabBroadcastChannel';
import { getOrCreateTabId, isLockExpired, PING_INTERVAL } from './utils';
import { emitSessionLifecycleEvent } from '../Authentication/sessionObservability';

/**
 * Hook detects whether the current tab is main tab,
 * attempts to automatically reclaim main tab if it main ping expires,
 * handles force-claiming the main tab via cross-tab communication.
 *
 * @returns {Object}
 * - `isMainTab` (`React.MutableRefObject<boolean>`)
 * - `forceClaimTab` (`() => void`): Function for force-claiming tab
 * - `isOutdatedRef` (`React.MutableRefObject<boolean>`): For cutting off tab usage after force-claim
 */
const useSingleTabGuard = () => {
  const [mainTabId, setMainTabId] = useLocalStorage<string | null>(constants.MAIN_WINDOW_ID, null, true);
  const [tabLockPing, setTabLockPing] = useLocalStorage<number | null>(constants.MAIN_WINDOW_PING, null, true);
  const tabId = getOrCreateTabId();
  const isMobile = getIsMobile();

  const isMainTabRef = useRef(tabId === mainTabId);
  const pingRef = useRef<number | null>();
  const isOutdatedRef = useRef<boolean>(false);
  const isIntervalActiveRef = useRef<boolean>(false);

  const [isMainTab, setIsMainTab] = useState(isMainTabRef.current);

  const onForceClaim = useCallback(
    (senderTabId: string) => {
      if (senderTabId === tabId) return;
      isOutdatedRef.current = true;
      sessionStorage.removeItem(constants.SESSION_WINDOW_ID);
      setIsMainTab(false);
      emitSessionLifecycleEvent({
        domain: 'tab',
        action: 'tab_outdated',
        is_main_tab: false,
      });
    },
    [tabId]
  );

  const claimTab = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    setMainTabId(tabId);
    setTabLockPing(Date.now());
    setIsMainTab(true);
  }, [setMainTabId, tabId, setTabLockPing]);

  const { sendForceClaim } = useTabBroadcastChannel({ tabId, onForceClaim });
  const forceClaimTab = useCallback(() => {
    emitSessionLifecycleEvent({
      domain: 'tab',
      action: 'tab_force_claim',
      is_main_tab: true,
    });
    logger.info('Force claiming tab');

    sendForceClaim();
    claimTab();
  }, [claimTab, sendForceClaim]);

  useEffect(() => {
    pingRef.current = tabLockPing;
  }, [tabLockPing]);

  useEffect(() => {
    isMainTabRef.current = isMainTab;
  }, [isMainTab]);

  useEffect(() => {
    const unload = () => setTabLockPing(null);
    if (isMainTab) window.addEventListener('unload', unload);

    return () => {
      window.removeEventListener('unload', unload);
    };
  }, [isMainTab, setTabLockPing]);

  useEffect(() => {
    const checkLock = () => {
      if (isOutdatedRef.current) return;
      if (isMainTabRef.current) return setTabLockPing(Date.now());

      const isTabLockExpired = isLockExpired(pingRef.current || 0);
      if (isTabLockExpired) isMobile || isSafari ? forceClaimTab() : claimTab();
    };

    let interval: number;
    if (!isIntervalActiveRef.current) {
      checkLock();
      isIntervalActiveRef.current = true;
      interval = setInterval(checkLock, PING_INTERVAL);
    }

    return () => {
      isIntervalActiveRef.current = false;
      clearInterval(interval);
    };
  }, [claimTab, forceClaimTab, isMobile, setTabLockPing]);

  return { isMainTab, forceClaimTab, isOutdatedRef };
};

export default useSingleTabGuard;
